// src/features/Reports/TrendReportPage.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useTheme,
  alpha,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  Download,
  Analytics,
  TrendingUp,
  Compare,
  PictureAsPdf,
  TableChart
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { getData } from '../../services/storageService';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart,
  Bar,
  Legend
} from 'recharts';

export default function TrendReportPage() {
  const { t } = useI18n();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [reportPeriod, setReportPeriod] = useState('30days');
  const [comparisonPeriod, setComparisonPeriod] = useState('previous');

  useEffect(() => {
    const loadData = async () => {
      try {
        const allRecords = await getData('lainsa_records');
        setRecords(Array.isArray(allRecords) ? allRecords : []);
      } catch (error) {
        console.error('Error loading report data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Аналіз даних для звіту
  const reportData = useMemo(() => {
    const shiftReports = records.filter(r => r.recordType === 'shift_report');
    
    if (shiftReports.length === 0) {
      return {
        currentPeriod: [],
        previousPeriod: [],
        trends: {
          production: 0,
          efficiency: 0,
          quality: 0
        },
        summary: {
          currentProduction: 0,
          previousProduction: 0,
          currentReports: 0,
          previousReports: 0
        }
      };
    }

    // Фільтрація за періодом
    const now = new Date();
    let currentStart = new Date();
    let previousStart = new Date();

    switch (reportPeriod) {
      case '7days':
        currentStart.setDate(now.getDate() - 7);
        previousStart.setDate(now.getDate() - 14);
        break;
      case '30days':
        currentStart.setDate(now.getDate() - 30);
        previousStart.setDate(now.getDate() - 60);
        break;
      case '90days':
        currentStart.setDate(now.getDate() - 90);
        previousStart.setDate(now.getDate() - 180);
        break;
      default:
        currentStart.setDate(now.getDate() - 30);
        previousStart.setDate(now.getDate() - 60);
    }

    const currentData = shiftReports.filter(r => new Date(r.fecha) >= currentStart);
    const previousData = shiftReports.filter(r => {
      const date = new Date(r.fecha);
      return date >= previousStart && date < currentStart;
    });

    // Розрахунок трендів
    const currentProduction = currentData.reduce((sum, r) => 
      sum + Object.values(r.tunel || {}).reduce((s, v) => s + Number(v || 0), 0), 0
    );
    
    const previousProduction = previousData.reduce((sum, r) => 
      sum + Object.values(r.tunel || {}).reduce((s, v) => s + Number(v || 0), 0), 0
    );

    const productionChange = previousProduction > 0 
      ? ((currentProduction - previousProduction) / previousProduction * 100)
      : currentProduction > 0 ? 100 : 0;

    return {
      currentPeriod: currentData,
      previousPeriod: previousData,
      trends: {
        production: productionChange,
        efficiency: 5.2, // Приклад
        quality: -2.1    // Приклад
      },
      summary: {
        currentProduction,
        previousProduction,
        currentReports: currentData.length,
        previousReports: previousData.length
      }
    };
  }, [records, reportPeriod]);

  // Генеруємо демо-дані для графіка
  const chartData = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      date: `${t('common_days', 'день')} ${i + 1}`,
      current: 1500 + Math.random() * 500,
      previous: 1400 + Math.random() * 400
    }));
  }, [t]);

  const handleExportPDF = () => {
    alert(t('forecast_development'));
  };

  const handleExportExcel = () => {
    alert(t('forecast_development'));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
          }}
        >
          <Analytics sx={{ color: 'white', fontSize: 32 }} />
        </Box>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
            {t('trend_reports_title')}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {t('trend_reports_subtitle')}
          </Typography>
        </Box>
      </Stack>

      {/* Контролі */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('reports_period')}</InputLabel>
              <Select
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
                label={t('reports_period')}
              >
                <MenuItem value="7days">{t('forecast_7days')}</MenuItem>
                <MenuItem value="30days">{t('forecast_30days')}</MenuItem>
                <MenuItem value="90days">90 {t('common_days')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('reports_comparison')}</InputLabel>
              <Select
                value={comparisonPeriod}
                onChange={(e) => setComparisonPeriod(e.target.value)}
                label={t('reports_comparison')}
              >
                <MenuItem value="previous">{t('reports_previous')}</MenuItem>
                <MenuItem value="lastYear">{t('reports_lastYear')}</MenuItem>
                <MenuItem value="target">{t('reports_target')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button startIcon={<PictureAsPdf />} variant="outlined" onClick={handleExportPDF}>
                {t('actions_exportPdf')}
              </Button>
              <Button startIcon={<TableChart />} variant="outlined" onClick={handleExportExcel}>
                {t('actions_exportXlsx')}
              </Button>
              <Button startIcon={<Download />} variant="contained">
                {t('reports_generate')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {Math.round(reportData.summary.currentProduction / 1000)}{t('common_ton')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('reports_production_period')}
              </Typography>
              <Chip 
                label={`${reportData.trends.production > 0 ? '+' : ''}${reportData.trends.production.toFixed(1)}${t('common_percent')}`}
                color={reportData.trends.production > 0 ? 'success' : 'error'}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'info.main' }}>
                {reportData.summary.currentReports}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('reports_reports_count')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main' }}>
                +5.2{t('common_percent')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('reports_efficiency')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'warning.main' }}>
                -2.1{t('common_percent')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('reports_quality')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Графік трендів */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
            {t('reports_production_trends')}
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="current" 
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                name={t('reports_current_period')}
              />
              <Line 
                type="monotone" 
                dataKey="previous" 
                stroke={theme.palette.secondary.main}
                strokeWidth={2}
                name={t('reports_previous_period')}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Таблиця даних */}
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
            {t('analytics_raw_data_table')}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>{t('labels_kpi')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{t('reports_current_period')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{t('reports_previous_period')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{t('reports_change')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{t('reports_total_production')}</TableCell>
                  <TableCell align="right">{Math.round(reportData.summary.currentProduction)} {t('common_kg')}</TableCell>
                  <TableCell align="right">{Math.round(reportData.summary.previousProduction)} {t('common_kg')}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={`${reportData.trends.production > 0 ? '+' : ''}${reportData.trends.production.toFixed(1)}${t('common_percent')}`}
                      color={reportData.trends.production > 0 ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('reports_reports_count')}</TableCell>
                  <TableCell align="right">{reportData.summary.currentReports}</TableCell>
                  <TableCell align="right">{reportData.summary.previousReports}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label="+8.5%"
                      color="success"
                      size="small"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('reports_average_efficiency')}</TableCell>
                  <TableCell align="right">85.2{t('common_percent')}</TableCell>
                  <TableCell align="right">80.0{t('common_percent')}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label="+5.2%"
                      color="success"
                      size="small"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('reports_rejection_rate')}</TableCell>
                  <TableCell align="right">2.1{t('common_percent')}</TableCell>
                  <TableCell align="right">4.2{t('common_percent')}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label="-2.1%"
                      color="success"
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}


